// /translate/embassy-legalization — destination-embassy legalization hub (TH)
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { createFileRoute } from "@tanstack/react-router";
import { CertTypeHub } from "@/components/translate/CertTypeHub";
import { getCertType } from "@/data/translation-certtypes";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { speakableSchema, serviceWithReviewsSchema, howToSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_TH } from "@/lib/trust-schema";

const SITE = "https://nyclanguageinstitute.com";
const cert = getCertType("embassy-legalization")!;

export const Route = createFileRoute("/translate/embassy-legalization")({
  head: () => {
    const url = `${SITE}/translate/embassy-legalization`;
    const title = `${cert.nameTh} | NYC Language Institute`;
    const desc = cert.taglineTh;
    return {
      meta: [
        { title: ctaTitle(title, "/translate/embassy-legalization", "th") }, { name: "description", content: ctaDesc(desc, "/translate/embassy-legalization", "th")},
        { name: "keywords", content: cert.keywords.join(", ") },
        { property: "og:title", content: ctaTitle(title, "/translate/embassy-legalization", "th")}, { property: "og:description", content: ctaDesc(desc, "/translate/embassy-legalization", "th")},
        { property: "og:url", content: url },
      ],
      links: canonicalAndHreflang("/translate/embassy-legalization", "/en/translate/embassy-legalization"),
      scripts: [
        speakableSchema(),
        howToSchema({
          name: "ขั้นตอน Embassy Legalization กับ NYC",
          description: cert.taglineTh,
          totalTime: "PT10D", estimatedCost: { currency: "THB", value: String(cert.priceFromThb) },
          steps: cert.stepsTh.map((s) => ({ name: s.name, text: s.text })),
        }),
        consolidateGraph([
          serviceWithReviewsSchema({ path: "/translate/embassy-legalization", serviceType: "Embassy Legalization", name: title, description: desc, priceFromThb: cert.priceFromThb }),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "FAQPage", "@id": `${url}#faq`,
              mainEntity: cert.faqsTh.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "แปลรายภาษา", item: `${SITE}/translate` },
                { "@type": "ListItem", position: 3, name: "Embassy Legalization", item: url },
              ],
            }),
          },
        ]),
        entitySameAsSchema(),
        softwareApplicationSchema({ url: `${SITE}/api/public/mcp`, name: "NYC Language Institute — MCP Server", description: "AI agent endpoint exposing embassy legalization tooling." }),
        claimReviewSchema(COMMON_CLAIM_REBUTTALS_TH(url)),
      ],
    };
  },
  component: () => <CertTypeHub cert={cert} locale="th" />,
});
